import UniDataTable from '@/common/classes/UniDataTable'
import { platformIsInstanceOf } from '@/common/utils/platformIsInstanceOf'

type EachCallback = (item: unknown, index: number) => unknown

function isThenable(value: unknown): value is PromiseLike<unknown> {
  return Boolean(value) && typeof (value as { then?: unknown }).then === 'function'
}

function collectEachItems(collection: unknown): unknown[] | null {
  if (collection == null) return null

  if (Array.isArray(collection) || platformIsInstanceOf(collection, Array)) {
    return [...(collection as unknown[])]
  }

  const rec = collection as {
    iterator?: () => { hasNext?: () => boolean; next?: () => unknown }
  }
  if (typeof rec.iterator === 'function') {
    const iterator = rec.iterator()
    if (iterator && typeof iterator.hasNext === 'function' && typeof iterator.next === 'function') {
      const items: unknown[] = []
      while (iterator.hasNext()) {
        items.push(iterator.next())
      }
      return items
    }
  }

  if (typeof (collection as { length?: unknown }).length === 'number') {
    if (typeof collection === 'string' || collection instanceof String) return null
    const arr = collection as { length: number; [index: number]: unknown }
    const items: unknown[] = []
    for (let i = 0; i < arr.length; ++i) {
      items.push(arr[i])
    }
    return items
  }

  if (collection instanceof UniDataTable || hasTableEachApi(collection)) {
    const table = collection as { size: () => number; getRow: (index: number) => unknown }
    const n = table.size()
    const items: unknown[] = []
    for (let i = 0; i < n; i++) {
      items.push(table.getRow(i))
    }
    return items
  }

  return null
}

/**
 * Swing fat-client `each` (sync). Thin-client import is async+await; formatPostalAddress
 * does not await `$("each")` and would return after the first segment.
 *
 * Sync callbacks still return a number immediately. If a callback returns a thenable
 * (Messages.* inside the callback), wait sequentially and return Promise<number>.
 */
export function platformEach(
  collection: unknown,
  func: EachCallback,
): number | Promise<number> {
  if (collection == null) return 0
  if (typeof func !== 'function') return 0

  const items = collectEachItems(collection)
  if (!items) return 0

  const n = items.length

  const continueAsync = async (
    pending: PromiseLike<unknown>,
    nextIndex: number,
  ): Promise<number> => {
    await pending
    for (let j = nextIndex; j < n; j++) {
      await func.apply(items[j], [items[j], j])
    }
    return n
  }

  for (let i = 0; i < n; ++i) {
    const result = func.apply(items[i], [items[i], i])
    if (isThenable(result)) {
      return continueAsync(result, i + 1)
    }
  }
  return n
}

function hasTableEachApi(collection: unknown): boolean {
  return Boolean(
    collection &&
      typeof collection === 'object' &&
      typeof (collection as { size?: unknown }).size === 'function' &&
      typeof (collection as { getRow?: unknown }).getRow === 'function',
  )
}
